import { useEffect, useState } from 'react';
import { InteractionType } from "@azure/msal-browser";
import { MsalAuthenticationTemplate, useMsal } from "@azure/msal-react";
import { Switch, Route, useLocation } from 'react-router-dom';
import { useClarity } from './hooks/useClarity';
import { ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import Cookies from 'universal-cookie';

import { loginRequest } from "./authConfig";

import NavbarTop from './components/Navbar';

import {
    DOTCoordinatorJobCodes,
    DOTEditorJobCodes,
    PMSMJobCodes,
} from './constants/DOTEditorsGroups';

import {
  hasRuleEditorAccess,
  hasAnyAdminAccess,
  hasQualityAdminAccess,
  hasTrainingTabAccess,
} from './functions/AccessHelpers';

import AdminPage from './pages/AdminPage';
import BenchmarkActivities from './pages/QualityAdmin/BenchmarkActivitiesPage';
import BenchmarkCreationGoal from './pages/ProjectGoals/BenchmarkCreationGoalPage';
import Dashboard from './pages/Dashboard';
import DashboardTaskDetails from './pages/DashboardTaskDetails';
import DataEditorHelp from './pages/DataSource/DataEditorHelp';
import DataEditorPage from './pages/DataSource/DataEditorPage';
import RuleEditorHelp from './pages/DataSource/RuleEditorHelp';
import RuleEditorPage from './pages/DataSource/RuleEditorPage';
import DOTCoordinatorsPage from './pages/DOTCoordinatorsPage';
import QualityControlPage from './pages/ProjectGoals/QualityControlPage';
import QualityFormApproversPage from './pages/QualityFormApproversPage';
import TaskDetailsPage from './pages/TaskDetailsPage';
import TasksPage from './pages/TasksPage';
import StandardWorkTrainer from './pages/TrainingPages/StandardWorkTrainer';
import WorkdayCertificationsPage from './pages/TrainingPages/WorkdayCertificationsPage';
import WorkdayTopicsPage from './pages/TrainingPages/WorkdayTopicsPage';
import UserInfoPage from './pages/UserInfoPage';

import DashboardService from "./services/DashboardService";
import DataService from './services/DataService';
import EmployeeService from './services/EmployeeService';
import FormsOnFireService from './services/FormsOnFire';
import TaskService from './services/TaskService';
import TestService from './services/TestService';

import ITask from './types/Task/Task';
import ITestGroup from './types/TestGroup';
import IProject from './types/Dashboard/Project';
import IProjectList from "./types/Dashboard/ProjectList";
import { DataSource } from "./types/Data/DataSource";
import IDataSourceQuery from "./types/Data/DataSourceQuery";
import IDataSourceQueryList from "./types/Data/DataSourceQueryList";
import IEmployeeInfo from './types/Employee/EmployeeInfo';
import EnvironmentData from './types/FormsOnFire/EnvironmentData';
import { EnvironmentQuery, MetaDataQuery } from './types/FormsOnFire/EnvironmentQuery';
import ListItem from './types/ListItem';

var tasks: ITask[];

function App() {
  const { accounts } = useMsal();
  const location = useLocation();
  const noProjectID = "N/A";

  const isTest = (process.env.REACT_APP_IS_TEST ?? '') === 'true';
  const isDevelop = (process.env.REACT_APP_IS_DEVELOP ?? '') === 'true';

  // Initialize Microsoft Clarity analytics
  const { identifyUser, setTag } = useClarity();

  async function getTasks(userEmail: string) {
    await TaskService.getTasks(userEmail)
    .then((response) => {
      tasks = response.data
    });
  }

  async function getSubcontractors() {
    try {
      // get headers to check for enterprise data
      const headers = (await DataService.getAllHeaders(process.env.REACT_APP_PRIMARY_FOF_ENVIRONMENT!)).data;

      // check for the enterprise ds in the list of headers

      const isEnterprise = !!headers.dataSources.find((x) => x.externalId === 'BOOMI_SUBCONTRACTORS_ENTERPRISE') ? true : false;

      let data;

      if (isEnterprise) {
        data = (await DataService.searchDataSourceData("BOOMI_SUBCONTRACTORS_ENTERPRISE", "1", "")).data;
      } else {
        data = (await DataService.searchDataSourceData("BOOMI_SUBCONTRACTORS", "1", "")).data;
      }

      return data;
    } catch (ex) {
      console.log("Exception Occured getting Subcontractor Data with error: ", ex);
    }
  }

  function updateTasks(newTasks: ITask[]) {
    tasks = newTasks;
  }

  const initEmployeeInfo: IEmployeeInfo = {
    employeeID: 0,
    employeeName: "",
    title: "",
    businessUnit: "",
    company: "",
    email: "",
    firstName: "",
    lastName: "",
    jobCode: "",
    employeeNameDisplay: "",
    adGroups: [],
    projects: [],
  }

  const initProjectList: IProjectList = {
    projects: [] as IProject[],
    defaultProjectID: noProjectID,
  }

  // This is only used to get the current logged in user information from the Portal API
  const contextEmail = accounts[0]?.username.toLowerCase(); // Lowercase avoids errors when comparing email to Blattner Employees DS

  // Initialize projectNumber from cookie if available
  const cookies = new Cookies();
  const [projectNumber, setProjectNumberState] = useState<string>(() => cookies.get('projects') ?? "");

  const [canEditDOT, setCanEditDOT] = useState(false);
  const [currentUserData, setCurrentUserData] = useState<IEmployeeInfo>(initEmployeeInfo);
  const [userAssignments, setUserAssignments] = useState<string[]>();

  const [testGroups, setTestGroups] = useState<ITestGroup[]>();
  const [testJobCode, setTestJobCode] = useState<string>('');
  const [useTestUser, setUseTestUser] = useState<boolean>(false);

  const [masterEmployeeList, setMasterEmployeeList] = useState<ListItem[]>([]);
  const [fetchingNames, setFetchingNames] = useState<boolean>(false);
  const [gettingUserData, setGettingUserData] = useState<boolean>(false);

  // these get the same thing
  const [projectList, setProjectList] = useState<IProjectList>(initProjectList);
  const [globalEnvironmentList, setGlobalEnvironmentList] = useState<EnvironmentData[]>();

  const [targetUserData, setTargetUserData] = useState<IEmployeeInfo>(initEmployeeInfo);

  /**
   * get the global environments list
   * if this is dev or test only allow access to updating:
   *   - Dev Test
   *   - Dev V.03
   *   - Staging
   */
  useEffect(() => {
    const DevTestEnvironment: EnvironmentData = {
      projectID: "68457",
      projectNumber: "978",
      projectName: "Dev Test",
    }

    const DevV03Environment: EnvironmentData = {
      projectID: "56824",
      projectName: "Dev V.03",
      projectNumber: "003",
    };

    const StagingEnvironment: EnvironmentData = {
      projectID: "71545",
      projectNumber: "978",
      projectName: "Blattner Mobile Staging",
    }

    const getEnvironments = async() => {
      try {
        const mdQueryList: MetaDataQuery[] = [
                {
                    key: 'Project Status',
                    regex: 'Active',
                },
            ];
        const query: EnvironmentQuery = {
            metaDataQueryList: mdQueryList,
        }
        const { data } = await FormsOnFireService.getEnvironment(query);

        data.unshift(DevV03Environment)

        setGlobalEnvironmentList(data);
      } catch (ex) {
        console.log('Exception getting environment data:', ex)
      }
    }
    if (isDevelop || isTest) {
      setGlobalEnvironmentList([DevV03Environment, DevTestEnvironment, StagingEnvironment]);

    } else {
      getEnvironments();
    }
  }, [isDevelop, isTest]);

  /**
   * Using this to catch the page load and get the hits for BOOMI EMPLOYEES in order
   *   this will reduce the number of API hits and hopefully make thing run a little faster
   */
  useEffect(() => {
    if (fetchingNames || masterEmployeeList?.length > 0 || contextEmail === undefined) {
      return;
    }
    setFetchingNames(true);
    setGettingUserData(true);

    const stackCallsToBoomiEmployees = async () => {
      try {
        // get headers to check for enterprise data
        const headers = (await DataService.getAllHeaders(process.env.REACT_APP_PRIMARY_FOF_ENVIRONMENT!)).data;

        // check for the enterprise ds in the list of headers
        const isEnterprise = !!headers.dataSources.find((x) => x.externalId === 'BOOMI_EMPLOYEES_ENTERPRISE') ? true : false;

        let employeeData: DataSource

        // get all users
        // we want to use the enterprise data if we have it otherwise default ot the legacy data
        if (isEnterprise) {
          employeeData = (await DataService.getDataSource('56824', 'BOOMI_EMPLOYEES_ENTERPRISE')).data;
        } else {
          employeeData = (await DataService.getDataSource('56824', 'BOOMI_EMPLOYEES')).data;
        }

        const usersList: ListItem[] = [];

        employeeData.rows
          .forEach((row) => {
            usersList.push({
              value: row[5], // email
              label: `${row[8]} ${row[9]} - ${row[0]}`, // first + last + employeeID
            })
          });

        setMasterEmployeeList(usersList.sort((a, b) => (a.label < b.label) ? -1 : (a.label > b.label) ? 1 : 0));

        // get User information
        const userResponse = await EmployeeService.getEmployeeInfo(contextEmail);
        const userInfo = userResponse.data
        userInfo.adGroups.sort((a, b) => (a.displayName < b.displayName)  ? -1 : (a.displayName > b.displayName) ? 1 : 0);
        setCurrentUserData(userInfo);
        setTargetUserData(userInfo);

      } catch (error) {
        console.log('Error getting the employee info:', error)
      } finally {
        setFetchingNames(false);
        setGettingUserData(false);
      }
    }

    stackCallsToBoomiEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextEmail])

  // Identify user in Clarity when user data is available
  useEffect(() => {
    if (currentUserData.email) {
      identifyUser({
        userId: currentUserData.email,
        friendlyName: currentUserData.employeeNameDisplay || currentUserData.employeeName,
      });

      // Set additional context tags
      if (currentUserData.jobCode) {
        setTag('jobCode', currentUserData.jobCode);
      }
      if (currentUserData.businessUnit) {
        setTag('businessUnit', currentUserData.businessUnit);
      }
    }
  }, [currentUserData, identifyUser, setTag]);

  useEffect(() => {
    const getTestGroups = async (userEmail: string) => {
      await TestService.getGroup(userEmail)
        .then((response) => {
          setTestGroups(response.data);
        });
    }

    getTestGroups(targetUserData.email);
  }, [targetUserData])

  /**
   * the next two useEffects can probably be consolidated into one because
   *   they both have the same dependency.
   */
  useEffect(() => {
    if (!targetUserData.email) {
      return;
    }

    const getProjectList = async (userEmail: string) => {
      await DashboardService.getUserProjects(userEmail)
        .then((response) => {
          setProjectList(response.data);
        })
        .catch((error) => {
          console.log("Error getting the project list", error);
        })
    }

    getProjectList(targetUserData.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUserData])

  useEffect(() => {
    if (targetUserData.employeeID === 0) {
      return;
    }

    const getUserAssignedProjects = async (employeeID: number) => {
      const query: IDataSourceQuery = {
        column: 0,
        values: [
          employeeID.toString(),
        ]
      }
      const queryList: IDataSourceQueryList = {
        query: [ query ]
      }
      /**
       * These updates can be removed to only use the "Enterprise" DS when we
       * feel we are safely past the transition period for submitting forms
       * with "Legacy" data
       */
      // adding the promise all to get both "Legacy" and "Enterprise" DS data
      try {

        // get headers to check for enterprise data
        const headers = (await DataService.getAllHeaders(process.env.REACT_APP_PRIMARY_FOF_ENVIRONMENT!)).data;

        // check for the enterprise ds in the list of headers
        const isEnterprise = !!headers.dataSources.find((x) => x.externalId === 'BOOMI_ASSIGNMENTS_ENTERPRISE') ? true : false;

        let data;

        if (isEnterprise) {
          data = (await DataService.searchDataSourceDataWithQuery("BOOMI_ASSIGNMENTS_ENTERPRISE", 3, queryList)).data;
        } else {
          data = (await DataService.searchDataSourceDataWithQuery("BOOMI_ASSIGNMENTS", 3, queryList)).data;
        }

        setUserAssignments(data);

      } catch (error) {
        console.log("Error getting assignments information", error);
      }
    }
    getUserAssignedProjects(targetUserData.employeeID);
  }, [targetUserData])

  useEffect(() => {
    if (targetUserData.email) {
      if (location.pathname !== "/tasks") {
        getTasks(targetUserData.email);
      }

      // Don't preload subcontractors in Test environment or when on the taskdetails page
      if (!isTest && !location.pathname.includes("/taskdetails")) {
        getSubcontractors();
      }
    }
  }, [targetUserData, isTest, location])

  useEffect(() => {
    if(
      !projectNumber
      || !userAssignments
      || targetUserData.employeeID === 0
      || projectList.defaultProjectID === noProjectID
    ) {
      return;
    }
    // using variable to "rename" the data to make it a little more consisant to the vernacular
    const userJobCode = targetUserData.jobCode; // ex. "BFM350"
    const companyID = projectNumber; // ex. 68457
    const projectJobNumber = projectList.projects?.find(p => p.projectID === companyID)?.projectNumber ?? "none"; // ex. "978"
    const jobNumber = targetUserData.businessUnit; // ex. "978"

    const canEditAnyProjectsDOTCoordinator = (jobCode: string) => DOTCoordinatorJobCodes.includes(jobCode);
    const canEditTheirProjectDOTCoordinator = (jobCode: string) => DOTEditorJobCodes.includes(jobCode);
    const isPMSM = (jobCode: string) => PMSMJobCodes.includes(jobCode);
    const isPMSMProject = (jobNumber: string) => userAssignments.includes(jobNumber);
    const isInTestGroup = () => testGroups?.some(x => (x.group === 'Project_Manager' || x.group === 'Site_Manager')) ?? false;

    const userCanEditDOT = (
      canEditAnyProjectsDOTCoordinator(userJobCode)
      || ((jobNumber === projectJobNumber)
        && canEditTheirProjectDOTCoordinator(userJobCode))
      || (isPMSM(userJobCode)
        && isPMSMProject(projectJobNumber))
      || (isInTestGroup()
        && isTest)
    );

    setCanEditDOT(userCanEditDOT);
  }, [projectList, projectNumber, userAssignments, targetUserData, testGroups, isTest])

  const setProjectNumber = async (project: string) => {
    if(!project) {
      return;
    }
    setProjectNumberState(project);
  }

  const handleProjectChange = (project: string | undefined) => {
    console.log('App.tsx - handleProjectChange - project:', project);
    // catch undefined projects
    if (!project) return;

    setProjectNumber(project);
  }

  const convertEnvironmentListToProjectList = () => {
    // for now we need to cast this into an IProject type
    // this should be fixed such that the ProjectDropDown Component
    // no longer takes an IProjectList but a list of EnvironmentData
    const projects: IProject[] = !globalEnvironmentList ?  [] : globalEnvironmentList?.map(({ projectID, projectName, projectNumber }) => {
      const project: IProject = {
        projectID,
        projectName,
        projectNumber
      };
      return project
    });

    const dseProjectList: IProjectList = {
      projects,
      defaultProjectID: '',
    }
    return dseProjectList;
  }

  const getUser = ({
    email,
    jobCode,
    adGroups,
    ...otherData
  }: IEmployeeInfo) => {
    const user = {
      email,
      jobCode: (!useTestUser ? jobCode : testJobCode),
      adGroups: (!useTestUser ? adGroups : testGroups ? testGroups.map((group) => group.id) : []),
      ...otherData,
    };
    return (user)
  };

  const setJobCode = (jobCode: string) => {
    setTestJobCode(jobCode)
  };

  const handleMockUserSelect = async (selectedUserEmail: string) => {
    try {
      const result = await EmployeeService.getEmployeeInfo(selectedUserEmail.toLowerCase());
      setTargetUserData(result.data);
    } catch (e: any) {
      console.log("error! ", e)
    }
  };

  return (
    <div className="App">
      <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
        <div className='app-nav-bar'>
          <NavbarTop
            userData={currentUserData}
            targetUserData={targetUserData}
            gettingUserData={gettingUserData}
            switchTargetUser={(selectedUserEmail: string) => {
              handleMockUserSelect(selectedUserEmail)
            }}
            logOutAsUser={() => {setTargetUserData(currentUserData)}}
          />
        </div>
          <div className='app-body d-flex justify-content-center overflow-auto'>
            <Switch>
              <Route
                exact
                path="/"
                component={(props: any) => (
                  <Dashboard {...props}
                    projects={projectList.projects}
                    userData={targetUserData}
                    cookieName='dashboard'
                  />
                )}
              />
              <Route path="/dashboardTaskDetails" component={(props: any) => <DashboardTaskDetails {...props} />} />
              <Route exact path="/tasks" component={() => <TasksPage currentUser={targetUserData} preloadedTasks={tasks} updateTasks={updateTasks} />} />
              <Route
                path="/taskdetails/:query"
                component={(props: any) =>
                  <TaskDetailsPage {...props} currentUser={targetUserData} isMocked={currentUserData.employeeID !== targetUserData.employeeID} />
                }
              />
              <Route
                path="/dotcoordinators"
                component={() => 
                  <DOTCoordinatorsPage
                    currentUser={targetUserData}
                    canEdit={canEditDOT}
                    isMocked={currentUserData.employeeID !== targetUserData.employeeID}
                    cookieName='dotCoordinators'
                  />
                }
              />
              <Route
                path="/qualityformapprovers"
                component={() => 
                  <QualityFormApproversPage
                    currentUser={targetUserData}
                    isMocked={currentUserData.employeeID !== targetUserData.employeeID}
                    cookieName='qualityFormApprovers'
                  />
                }
              />
              <Route
                path="/projectGoals/benchmarkCreationGoal"
                render={() => (
                  <div className='benchmark-creation-goal-page'>
                    {
                    <BenchmarkCreationGoal
                      userData={targetUserData}
                      isMocked={currentUserData.employeeID !== targetUserData.employeeID}
                      projects={projectList.projects}
                      cookieName='benchmarkGoalNew'
                    />
                   }
                  </div>
                )}
              />
              <Route
                path="/projectGoals/qualityControl"
                component={() =>
                  <QualityControlPage
                    userData={currentUserData}
                    isMocked={currentUserData.employeeID !== targetUserData.employeeID}
                    projects={projectList.projects}
                    cookieName='benchmarkGoalNew'
                  />
                }
              />
              <Route path="/dataEditor/dataEditorHelp" component={() => (<div className='mt-4'><DataEditorHelp /></div>)} />
              { globalEnvironmentList &&
                <Route exact path="/dataEditor"
                  component={() => (
                    <DataEditorPage
                      globalEnvironmentList={globalEnvironmentList}
                      actualUser={currentUserData}
                      userData={getUser(targetUserData)}
                      isMocked={currentUserData.employeeID !== targetUserData.employeeID}
                      projects={projectList.projects}
                      cookieName='dataSourceEditor'
                    />
                  )}
                />
              }
              { hasTrainingTabAccess(targetUserData) &&
                <Route
                  path="/training/StandardWorkTrainer"
                  component={() => (
                    <div className='mt-4'>
                      <StandardWorkTrainer
                        userData={targetUserData}
                        isMocked={currentUserData.employeeID !== targetUserData.employeeID}
                        projectList={projectList.projects}
                      />
                    </div>
                  )}
                />
              }
              { hasTrainingTabAccess(targetUserData) &&
                <Route
                  path="/training/TrainingTopics"
                  component={() => (
                    <div className='mt-4'>
                      <WorkdayTopicsPage
                        userData={targetUserData}
                        isMocked={currentUserData.employeeID !== targetUserData.employeeID}
                      />
                    </div>
                  )}
                />
              }
              { hasTrainingTabAccess(targetUserData) &&
                <Route
                  path="/training/Certificates"
                  component={() => (
                    <div className='mt-4'>
                      <WorkdayCertificationsPage
                        userData={targetUserData}
                        isMocked={currentUserData.employeeID !== targetUserData.employeeID}
                      />
                    </div>
                  )}
                />
              }
              { hasQualityAdminAccess(targetUserData) &&
                <Route
                  path="/qualityAdmin/benchmarkActivities"
                  component={() => (
                    <div className='mt-4'>
                      <BenchmarkActivities
                        userData={targetUserData}
                        isMocked={currentUserData.employeeID !== targetUserData.employeeID}
                      />
                    </div>
                  )}
                />
              }
              <Route path="/ruleEditor/ruleEditorHelp" component={() => (<div className='mt-4'><RuleEditorHelp /></div>)} />
              {(hasRuleEditorAccess(targetUserData)) &&
                <Route exact path="/ruleEditor"
                  component={() => (
                    <RuleEditorPage
                      userData={currentUserData}
                      projectList={convertEnvironmentListToProjectList()}
                      masterEmployeeList={masterEmployeeList}
                    />
                  )}
                />
              }
              {hasAnyAdminAccess(targetUserData)
                && (<Route exact path="/admin"
                  component={() => (
                    <AdminPage
                      preloadedGroups={testGroups}
                      updateTestGroups={setTestGroups}
                      currentTestJobCode={testJobCode}
                      updateTestJobCode={setJobCode}
                      updateUseTestUser={() => setUseTestUser(!useTestUser)}
                      currentJobCode={testJobCode}
                      usingTestUser={useTestUser}
                      userData={currentUserData}
                      cookieName='adminPages'
                      projects={projectList.projects}
                    />
                  )}
                />
              )}
              <Route exact path="/userInfo"
                component={() => (
                  <UserInfoPage
                    userData={targetUserData}
                  />
                )}
              />
            </Switch>
          </div>
      </MsalAuthenticationTemplate>
      <ToastContainer />
    </div>
  );
}

export default App;